<template>
  <div>
    <ifac-loader v-if="loading" />

    <template v-else>
      <b-table
        small
        responsive
        bordered
        :fields="fields"
        :items="rows"
        class="mb-2"
      >
        <template #cell(sourceTcId)="{ item }">
          <span v-if="isCcOriginatedAdd(item)" class="text-muted">—</span>
          <template v-else>
            TC{{ tcLabel(item.sourceTcId) }}
          </template>
        </template>

        <template #cell(existingKeywordName)="{ item }">
          {{ item.existingKeywordName || '—' }}
        </template>

        <template #cell(tcProposedName)="{ item }">
          <span :class="warningClass(item.rowId, 'tcProposedName')">
            {{ item.tcProposedName || '—' }}
          </span>
        </template>

        <template #cell(ccAction)="{ item }">
          <span
            v-if="isCcOriginatedAdd(item)"
            class="text-muted"
          >
            Add
          </span>
          <b-form-select
            v-else
            v-model="item.ccAction"
            :options="actionOptions(item)"
            size="sm"
            style="min-width: 8rem;"
            @change="onCcActionChange(item)"
          />
        </template>

        <template #cell(targetTcId)="{ item }">
          <b-form-select
            v-if="showTargetTc(item)"
            v-model="item.targetTcId"
            :options="technicalCommitteeOptions"
            size="sm"
            value-field="id"
            text-field="label"
            style="min-width: 8rem;"
            @change="onTargetTcChange(item)"
          />
          <span v-else>TC{{ tcLabel(item.targetTcId) }}</span>
        </template>

        <template #cell(ccProposedName)="{ item }">
          <span
            v-if="showsDashResult(item)"
            class="text-muted"
          >
            —
          </span>
          <b-form-input
            v-else
            v-model="item.ccProposedName"
            size="sm"
            :class="warningClass(item.rowId, 'ccProposedName')"
            style="min-width: 18rem;"
            @input="onCcProposedNameInput(item)"
          />
        </template>

        <template #cell(similar)="{ item }">
          <b-button
            v-if="isCcOriginatedAdd(item)"
            size="sm"
            variant="link"
            class="px-1 text-nowrap text-danger"
            @click="removeDraftRow(item)"
          >
            Remove
          </b-button>
          <b-button
            size="sm"
            variant="link"
            class="px-1 text-nowrap"
            :disabled="!proposedNameForSimilarity(item) || similarBusy"
            @click="$emit('see-similar', item)"
          >
            See similar keywords
          </b-button>
        </template>
      </b-table>

      <b-button size="sm" variant="outline-primary" @click="addRow">
        Add keyword
      </b-button>
    </template>
  </div>
</template>

<script>
import { IfacLoader } from '@ifac/ui';
import Revisions from '@/services/Api/TechnicalAreas/Revisions';

export default {
  name: 'CcKeywordsReviewTable',
  components: {
    IfacLoader,
  },
  props: {
    revisionId: {
      type: String,
      required: true,
    },
    coordinatingCommitteeId: {
      type: [Number, String],
      required: true,
    },
    similarBusy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      rows: [],
      warnings: {},
      technicalCommittees: [],
      fields: [
        { key: 'sourceTcId', label: 'Prev. TC', thStyle: { minWidth: '5.5rem' } },
        { key: 'existingKeywordName', label: 'Existing keyword', thStyle: { minWidth: '12rem' } },
        { key: 'tcProposedName', label: 'Proposed by TC Chair', thStyle: { minWidth: '12rem' } },
        { key: 'ccAction', label: 'Action', thStyle: { minWidth: '8rem' } },
        { key: 'targetTcId', label: 'Next TC', thStyle: { minWidth: '8rem' } },
        { key: 'ccProposedName', label: 'Proposed by CC Chair', thStyle: { minWidth: '18rem' } },
        { key: 'similar', label: '', thStyle: { minWidth: '10rem' } },
      ],
    };
  },
  computed: {
    technicalCommitteeOptions() {
      return this.technicalCommittees.map((tc) => ({
        id: tc.id,
        label: tc.label ? `TC${tc.label}` : tc.name,
      }));
    },
  },
  async created() {
    await this.loadReview();
  },
  methods: {
    async loadReview() {
      this.loading = true;

      try {
        const response = await Revisions.getCcKeywordReview(
          this.revisionId,
          this.coordinatingCommitteeId,
        );
        const { data } = response.data;
        this.rows = data.rows || [];
        this.warnings = data.warnings || {};
        this.technicalCommittees = data.technicalCommittees || [];
      } catch (error) {
        this.rows = [];
        this.warnings = {};
        this.technicalCommittees = [];
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not load CC keyword review.',
        });
      } finally {
        this.loading = false;
      }
    },
    isCcOriginatedAdd(row) {
      return Revisions.isCcOriginatedAdd(row);
    },
    tcLabel(technicalCommitteeId) {
      const match = this.technicalCommittees.find(
        (tc) => Number(tc.id) === Number(technicalCommitteeId),
      );

      return match?.label || technicalCommitteeId;
    },
    actionOptions() {
      return Revisions.ccActionOptions();
    },
    showTargetTc(row) {
      return !this.showsDashResult(row);
    },
    showsDashResult(row) {
      // Accepting a TC removal approves omitting the keyword.
      if (row.ccAction === 'accept' && row.tcAction === 'remove') {
        return true;
      }

      // Rejecting a proposed add omits the keyword.
      return row.ccAction === 'reject' && !row.existingKeywordId;
    },
    onCcActionChange(row) {
      if (row.ccAction === 'accept') {
        row.targetTcId = row.sourceTcId;

        if (row.tcAction === 'remove') {
          row.ccProposedName = '';
        } else {
          row.ccProposedName = row.tcProposedName;
        }
      }

      if (row.ccAction === 'reject') {
        // Deny TC suggestion: restore existing keyword, or clear a proposed add.
        row.ccProposedName = row.existingKeywordId
          ? (row.existingKeywordName || '')
          : '';
        row.targetTcId = row.sourceTcId;
      }

      if (row.ccAction === 'modify' && !row.ccProposedName) {
        row.ccProposedName = row.tcProposedName || row.existingKeywordName || '';
      }
    },
    onCcProposedNameInput(row) {
      if (this.isCcOriginatedAdd(row)) {
        return;
      }

      Revisions.applyCcActionFromProposedName(row);
    },
    onTargetTcChange(row) {
      if (this.isCcOriginatedAdd(row)) {
        row.sourceTcId = row.targetTcId;
        return;
      }

      Revisions.applyCcActionFromProposedName(row);
    },
    addRow() {
      if (!this.technicalCommittees.length) {
        this.$snack.danger({ text: 'No technical committees available to add a keyword.' });
        return;
      }

      this.rows.push(Revisions.buildCcOriginatedAddRow({
        technicalCommittees: this.technicalCommittees,
      }));
    },
    removeDraftRow(row) {
      const index = this.rows.findIndex((candidate) => candidate.rowId === row.rowId);

      if (index === -1) {
        return;
      }

      this.rows.splice(index, 1);
    },
    proposedNameForSimilarity(row) {
      if (this.showsDashResult(row)) {
        return '';
      }

      return row.ccProposedName || row.tcProposedName;
    },
    warningClass(rowId, column) {
      return Revisions.warningClass(this.warnings, rowId, column);
    },
    getReviews() {
      return this.rows;
    },
    async save() {
      const response = await Revisions.updateCcKeywordReview(
        this.revisionId,
        this.coordinatingCommitteeId,
        this.rows,
      );

      const data = await Revisions.getCcKeywordReview(
        this.revisionId,
        this.coordinatingCommitteeId,
      );
      this.rows = data.data.data.rows || this.rows;
      this.warnings = data.data.data.warnings || {};

      return response.data.data;
    },
  },
};
</script>
